import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Fab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  ListItemText,
} from '@mui/material'
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Update as UpdateIcon,
  CloudSync as CloudSyncIcon,
  Inventory as MasterAwbIcon,
  Clear as ClearIcon,
  GoogleSheetsIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material'
import dayjs from 'dayjs'

import ShipmentForm from '../components/ShipmentForm'
import MasterAWBCard from '../components/MasterAWBCard'
import OrderIdSearch from '../components/OrderIdSearch'
import ShipmentTable from '../components/ShipmentTable'
import GoogleSheetsOAuthDialog from '../components/GoogleSheetsOAuthDialog'

import { apiService } from '../services/apiService'

const Dashboard = ({ projectId }) => {
  const [masterAwbs, setMasterAwbs] = useState([])
  const [filteredMasterAwbs, setFilteredMasterAwbs] = useState([])
  const [pagination, setPagination] = useState({ total: 0, skip: 0, limit: 100, has_more: false })
  const [loading, setLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState({ tracking: false, delivery: false })
  const [message, setMessage] = useState({ type: '', text: '' })
  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [showGoogleSheets, setShowGoogleSheets] = useState(false)
  const [projects, setProjects] = useState([])
  
  // Initialize with current month and empty statuses
  const getCurrentMonth = () => {
    const now = dayjs()
    return {
      month: now.month() + 1, // dayjs months are 0-indexed
      year: now.year(),
      statuses: [] // Multi-select for statuses
    }
  }

  const [filters, setFilters] = useState(getCurrentMonth)

  // Load all shipments on component mount and filter changes
  useEffect(() => {
    if (projectId) {
      loadMasterAwbs()
    }
  }, [projectId, filters.month, filters.year, filters.statuses])

  // Show message if no project is selected
  if (!projectId) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          📦 Welcome to Shipment Tracking Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please select a project from the dropdown above to get started.
        </Typography>
      </Box>
    );
  }
  // Load Master AWBs for cards
  const loadMasterAwbs = async () => {
    try {
      const filterParams = {}
      if (filters.month) {
        filterParams.month = filters.month
      }
      if (filters.year) {
        filterParams.year = filters.year
      }
      if (filters.statuses && filters.statuses.length > 0) {
        filterParams.statuses = filters.statuses.join(',')
      }
      const response = await apiService.getMasterAWBs(projectId, filterParams)
      
      // Handle new paginated response format
      if (response.data && response.pagination) {
        setMasterAwbs(response.data)
        setFilteredMasterAwbs(response.data)
        setPagination(response.pagination)
      } else {
        // Fallback for old format (backward compatibility)
        setMasterAwbs(response)
        setFilteredMasterAwbs(response)
        setPagination({ total: response.length, skip: 0, limit: 100, has_more: false })
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to load Master AWBs: ${error.message}` })
    }
  }

  const loadMoreMasterAwbs = async () => {
    try {
      setLoading(true)
      const filterParams = {
        skip: pagination.skip + pagination.limit,
        limit: pagination.limit
      }
      
      if (filters.month) {
        filterParams.month = filters.month
      }
      if (filters.year) {
        filterParams.year = filters.year
      }
      if (filters.statuses && filters.statuses.length > 0) {
        filterParams.statuses = filters.statuses.join(',')
      }
      
      const response = await apiService.getMasterAWBs(projectId, filterParams)
      
      // Handle new paginated response format
      if (response.data && response.pagination) {
        // Append new data to existing data
        setMasterAwbs(prev => [...prev, ...response.data])
        setFilteredMasterAwbs(prev => [...prev, ...response.data])
        setPagination(response.pagination)
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to load more Master AWBs: ${error.message}` })
    } finally {
      setLoading(false)
    }
  }

  const loadAllShipments = async (preserveMessage = false) => {
    if (!projectId) return;
    
    setLoading(true)
    try {
      const filterParams = {}
      
      if (filters.startDate) {
        filterParams.start_date = filters.startDate.format('YYYY-MM-DD')
      }
      if (filters.endDate) {
        filterParams.end_date = filters.endDate.format('YYYY-MM-DD')
      }
      if (filters.month) {
        filterParams.month = filters.month
      }
      if (filters.year) {
        filterParams.year = filters.year
      }

      const shipmentsData = await apiService.getShipments(projectId, filterParams)

      setAllShipments(shipmentsData)
      setFilteredShipments(shipmentsData)
      
      if (!preserveMessage) {
        if (shipmentsData.length === 0) {
          setMessage({ 
            type: 'info', 
            text: 'No shipments found for the selected filters. Try adjusting your search criteria.' 
          })
        } else {
          setMessage({ type: '', text: '' })
        }
      }
      
    } catch (error) {
      if (!preserveMessage) {
        setMessage({ type: 'error', text: `Failed to load shipments: ${error.message}` })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }
  
  const resetToCurrentMonth = () => {
    setFilters(getCurrentMonth())
  }
  
  const clearAllFilters = () => {
    setFilters({
      month: null,
      year: null,
      statuses: []
    })
  }

  const handleUpdateTracking = async () => {
    if (!projectId) {
      setMessage({ type: 'error', text: 'Please select a project first' })
      return
    }

    setLoading(true)
    try {
      const result = await apiService.updateTracking(projectId)
      setMessage({ 
        type: 'success', 
        text: result.message || `Tracking update started for ${result.updated_count} shipments`
      })
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `Failed to update tracking: ${error.message}` 
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateDelivery = async () => {
    if (!projectId) {
      setMessage({ type: 'error', text: 'Please select a project first' })
      return
    }

    setLoading(true)
    try {
      const result = await apiService.updateDeliveryDates(projectId)
      setMessage({ 
        type: 'success', 
        text: result.message || `Delivery date update started for ${result.updated_count} shipments`
      })
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `Failed to update delivery dates: ${error.message}` 
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateAwbTracking = async () => {
    if (!projectId) {
      setMessage({ type: 'error', text: 'Please select a project first' })
      return
    }

    setLoading(true)
    try {
      const result = await apiService.updateAwbTracking(projectId)
      setMessage({ 
        type: 'success', 
        text: result.message || `AWB tracking update started for ${result.updated_count} AWBs`
      })
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `Failed to update AWB tracking: ${error.message}` 
      })
    } finally {
      setLoading(false)
    }
  }

  const handleFormSuccess = (data) => {
    setMessage({ 
      type: 'success', 
      text: `Successfully created Master AWB: ${data.master_awb_number}!` 
    })
    setShowForm(false)
    loadMasterAwbs()
  }

  const loadProjects = async () => {
    try {
      const data = await apiService.getAdminProjects()
      setProjects(data)
    } catch (error) {
      console.error('Failed to load projects:', error)
    }
  }

  // Filter Master AWBs by search query (order ID, Master AWB, Child AWB)
  const handleOrderIdSearch = (query) => {
    setSearchQuery(query)
    if (!query) {
      setFilteredMasterAwbs(masterAwbs)
      setMessage({ type: '', text: '' })
      return;
    }
    const queryLower = query.toLowerCase()
    const filtered = masterAwbs.filter(masterAwb => {
      // Check Master AWB number
      if (masterAwb.master_awb_number && masterAwb.master_awb_number.toLowerCase().includes(queryLower)) {
        return true;
      }
      // Check Child AWB numbers
      if (masterAwb.child_awbs && masterAwb.child_awbs.some(childAwb => 
        childAwb.child_awb_number && childAwb.child_awb_number.toLowerCase().includes(queryLower)
      )) {
        return true;
      }
      // Check shipments (order IDs)
      if (masterAwb.shipments && masterAwb.shipments.some(shipment =>
        shipment.order_id && shipment.order_id.toString().toLowerCase().includes(queryLower)
      )) {
        return true;
      }
      return false;
    });
    setFilteredMasterAwbs(filtered)
    if (filtered.length === 0) {
      setMessage({ type: 'info', text: `No results found for search: "${query}"` })
    } else {
      setMessage({ type: '', text: '' })
    }
  };

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ]

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MasterAwbIcon />
          Master AWB Tracking Dashboard
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={updateLoading.tracking ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleUpdateTracking}
            disabled={updateLoading.tracking}
          >
            Update Tracking
          </Button>
          
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} /> : <MasterAwbIcon />}
            onClick={handleUpdateAwbTracking}
            disabled={loading}
          >
            Update AWB Tracking
          </Button>
          
          <Button
            variant="outlined"
            startIcon={updateLoading.delivery ? <CircularProgress size={20} /> : <CloudSyncIcon />}
            onClick={handleUpdateDelivery}
            disabled={updateLoading.delivery}
          >
            Update Delivery
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select
                value={filters.month || ''}
                label="Month"
                onChange={(e) => handleFilterChange('month', e.target.value)}
              >
                {months.map(month => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={filters.year || ''}
                label="Year"
                onChange={(e) => handleFilterChange('year', e.target.value)}
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Shipment Status</InputLabel>
              <Select
                multiple
                value={filters.statuses || []}
                label="Shipment Status"
                onChange={(e) => handleFilterChange('statuses', e.target.value)}
                renderValue={(selected) => selected.join(', ')}
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="SHIPPED">Shipped</MenuItem>
                <MenuItem value="DELIVERED">Delivered</MenuItem>
                <MenuItem value="IN_TRANSIT">In Transit</MenuItem>
                <MenuItem value="EXCEPTION">Exception</MenuItem>
                <MenuItem value="RETURNED">Returned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              onClick={resetToCurrentMonth}
              fullWidth
              size="small"
            >
              Current Month
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              onClick={clearAllFilters}
              fullWidth
              size="small"
              color="secondary"
              startIcon={<ClearIcon />}
            >
              Clear All
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Order ID Search */}
      <Box sx={{ mb: 3 }}>
        <OrderIdSearch onSearch={handleOrderIdSearch} />
      </Box>

      {/* Messages */}
      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Master AWB Cards (filtered by search) */}
      {!loading && filteredMasterAwbs.length > 0 && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MasterAwbIcon />
            Master AWBs ({pagination.total || filteredMasterAwbs.length})
          </Typography>
          {filteredMasterAwbs.map((masterAwb) => (
            <MasterAWBCard
              key={masterAwb.id}
              masterAwb={masterAwb}
              onRefresh={loadMasterAwbs}
              searchQuery={searchQuery}
              selectedStatuses={filters.statuses || []}
            />
          ))}
          
          {/* Load More Button */}
          {pagination.has_more && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={loadMoreMasterAwbs}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : `Load More (${pagination.total - filteredMasterAwbs.length} remaining)`}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Floating Action Button */}
      <Tooltip title="Add New Master AWB">
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
          onClick={() => setShowForm(true)}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* Create Master AWB Dialog */}
      <Dialog
        open={showForm}
        onClose={() => setShowForm(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Create New Master AWB</DialogTitle>
        <DialogContent>
          <ShipmentForm
            projectId={projectId}
            onSubmitSuccess={handleFormSuccess}
            onError={(error) => {
              setMessage({ 
                type: 'error', 
                text: error.response?.data?.detail || 'Failed to create Master AWB' 
              })
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowForm(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Sheets OAuth Dialog */}
      <GoogleSheetsOAuthDialog 
        open={showGoogleSheets} 
        onClose={() => setShowGoogleSheets(false)} 
        projects={projects}
      />
    </Box>
  )
}

export default Dashboard